import React, { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import type { User } from '../../../shared/contracts';

/**
 * Global lock status checker for web
 * Checks if user is locked periodically
 * Should be placed at the root level of the app
 */
export function GlobalLockStatusChecker() {
  const { user, updateUser } = useAuth();
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Check lock status via API
  const checkLockStatus = async () => {
    if (!user) return;

    try {
      const authToken = localStorage.getItem('auth_token');
      if (!authToken) return;

      const response = await api.get<{ user: User }>('/auth/me', {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (response.user.isLocked && !user.isLocked) {
        console.log('[GlobalLockStatusChecker] User is now locked');
        updateUser({ ...response.user, isLocked: true });
        localStorage.setItem('user', JSON.stringify({ ...response.user, isLocked: true }));
      } else if (!response.user.isLocked && user.isLocked) {
        console.log('[GlobalLockStatusChecker] User is now unlocked');
        updateUser(response.user);
        localStorage.setItem('user', JSON.stringify(response.user));
      }
    } catch (error: any) {
      // Handle 403 locked
      if (error?.status === 403 && error?.message?.toLowerCase().includes('lock')) {
        console.log('[GlobalLockStatusChecker] User is locked (403)');
        const lockedUser = { ...user, isLocked: true };
        updateUser(lockedUser);
        localStorage.setItem('user', JSON.stringify(lockedUser));
        return;
      }

      // Silently handle other errors
      if (error?.status === 401) {
        console.log('[GlobalLockStatusChecker] User unauthorized');
      }
    }
  };

  // Poll periodically
  useEffect(() => {
    if (!user) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      return;
    }

    // Check immediately
    checkLockStatus();

    // Poll every 10 seconds
    pollIntervalRef.current = setInterval(checkLockStatus, 10000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [user?.id]);

  return null;
}
